import { useMutation, useQueryClient } from '@tanstack/react-query';
import { router, useNavigationContainerRef } from 'expo-router';
import { useCallback, useState } from 'react';

import { getCartQuery } from '@/api/booking/getCartQuery';
import { updateNotificationConsentMutation } from '@/api/booking/updateNotificationConsentMutation';
import type { CartCustomerWithLanguage } from '@/api/booking/upsertCartMutation/types';
import type { NotificationConsentChoice } from '@/components/booking/BookingConsentSheet/types';
import { useCheckoutSideEffects } from '@/hooks/booking/useCheckoutSideEffects';
import { classifyCheckoutError } from '@/utils/checkoutErrors';
import { Logger } from '@/utils/logging';
import { resetToViewReservation } from '@/utils/navigationUtils';

interface UseCheckoutBookingOptions {
  shopId: string;
  /** The shop's route slug — see `useCheckoutSideEffects`. */
  shopRoute?: string;
  /** The booking shop's GA4 Measurement ID — see `useCheckoutSideEffects`. */
  shopGaMeasurementId?: string | null;
  /** The booking shop's Meta pixel id — see `useCheckoutSideEffects`. */
  shopMetaPixelId?: string | null;
  cartId: string;
  customer: CartCustomerWithLanguage;
  addPayment: (payment: null) => void;
  onCheckoutStart?: () => void;
}

export const useCheckoutBooking = ({
  shopId,
  shopRoute,
  shopGaMeasurementId,
  shopMetaPixelId,
  cartId,
  customer,
  addPayment,
  onCheckoutStart,
}: UseCheckoutBookingOptions) => {
  const navigationRef = useNavigationContainerRef();
  const queryClient = useQueryClient();
  const [isPayInPerson, setIsPayInPerson] = useState(false);
  const [payInPersonToken, setPayInPersonToken] = useState<string | null>(null);
  const [isCartExpired, setIsCartExpired] = useState(false);
  const [notificationConsent, setNotificationConsent] =
    useState<NotificationConsentChoice>({});

  const { mutate: updateNotificationConsent } = useMutation(
    updateNotificationConsentMutation(),
  );

  // Accumulate the per-channel Yes/Skip choices as the user steps through the
  // consent cards (the sheet reports one channel at a time). Memoized so the
  // reference stays stable when passed down as a prop (matches
  // useGiftCardCheckoutFlow).
  const recordNotificationConsent = useCallback(
    (consent: NotificationConsentChoice) =>
      setNotificationConsent((prev) => ({ ...prev, ...consent })),
    [],
  );

  const {
    checkoutCartAsync,
    isCheckingOutCart,
    isCheckoutError,
    checkoutCartError,
    resetCheckout,
  } = useCheckoutSideEffects({
    shopId,
    shopRoute,
    shopGaMeasurementId,
    shopMetaPixelId,
    cartId,
    customer,
    onSuccess: (checkoutData) => {
      const customerId = checkoutData.payingCustomer?.id;
      router.setParams({ cart: undefined });

      if (customerId && Object.keys(notificationConsent).length > 0) {
        updateNotificationConsent({
          shopId,
          promotional: notificationConsent,
          customerId,
        });
      }

      requestAnimationFrame(() => {
        // popToTop() + push() surfaced the Home ("/") root screen between the
        // processing overlay and view-reservation: popToTop animated back to the
        // root, THEN push slid view-reservation in. Reset the stack to
        // [index, view-reservation] in a single dispatch so Home is placed
        // underneath (preserving the clean back-to-Home stack popToTop gave us)
        // without ever being the settled visible screen. The CashApp redirect
        // flow's rAF deferral (SQR-12752) is preserved.
        resetToViewReservation(navigationRef, {
          saleOrderId: checkoutData.id || '',
        });
      });
    },
    onError: () => {
      addPayment(null);
    },
  });

  const handleConsentContinue = async () => {
    if (!customer.email) {
      Logger.error('Customer is required to checkout', { customer });
      // SQR-15545: drop the payment attached before consent so the cart stays bookable
      addPayment(null);
      return;
    }

    // Validate the cart still exists on the server before attempting checkout.
    // The cart may have expired or been consumed if the user took too long.
    try {
      const freshCart = await queryClient.fetchQuery({
        ...getCartQuery(shopId, cartId),
        staleTime: 0,
      });

      if (!freshCart) {
        Logger.error('Cart not found during checkout validation', {
          shopId,
          cartId,
        });
        setIsCartExpired(true);
        return;
      }
    } catch (error) {
      Logger.error('Failed to validate cart before checkout', {
        error,
        shopId,
        cartId,
      });
      setIsCartExpired(true);
      return;
    }

    // Signal that checkout has started to prevent cart mutations
    // after the cart is consumed by checkout
    onCheckoutStart?.();

    // Determine the correct checkout flags based on payment scenario:
    // - isBookNoPay: true = Pay in person without card (requires barber.bookNoPay enabled)
    // - isReservation: true = Pay in person with card on file (for no-show protection)
    const isPayInPersonWithCard = isPayInPerson && payInPersonToken !== null;
    const isPayInPersonWithoutCard = isPayInPerson && payInPersonToken === null;

    // The mutation's onError already surfaces the failure (checkoutCartError,
    // decline tracking), and the consent sheet fires this without awaiting.
    try {
      return await checkoutCartAsync({
        isBookNoPay: isPayInPersonWithoutCard,
        isReservation: isPayInPersonWithCard,
        customer,
      });
    } catch {
      return undefined;
    }
  };

  const handleResetCheckout = () => {
    resetCheckout();
    setIsPayInPerson(false);
    setPayInPersonToken(null);
    setIsCartExpired(false);
  };

  const checkoutErrorType = checkoutCartError
    ? classifyCheckoutError(checkoutCartError)
    : null;

  return {
    isPayInPerson,
    setIsPayInPerson,
    payInPersonToken,
    setPayInPersonToken,
    isCheckingOutCart,
    isCheckoutError,
    isCartExpired,
    checkoutCartError,
    checkoutErrorType,
    handleConsentContinue,
    handleResetCheckout,
    notificationConsent,
    recordNotificationConsent,
  };
};
